import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ProfileContext } from "../context/ProfileContext";
import profileApi from "../services/profileApi";
import { BiSolidUser } from "react-icons/bi";
import { IoArrowBack, IoCalendarOutline, IoCameraOutline, IoLogOutOutline } from "react-icons/io5";
import Cookies from "js-cookie";
import Toast from "../components/Toast";
import translations from "../i18n/profile";
import { currentCountry, isMongolia } from "../utils/country";

const t = translations[currentCountry];

const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: 85 }, (_, i) => CURRENT_YEAR - 16 - i);
const MAX_PHOTO_SIZE = 50 * 1024 * 1024; // 50MB

const SELECT_STYLE = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: "right 0.5rem center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "1.5em 1.5em",
  paddingRight: "2.5rem",
};

// --- Custom Hooks ---

function useProfileForm(profile, { isTelegramUser, needsPhone }) {
  const [formData, setFormData] = useState({
    name: "", lastName: "", email: "", phone: "", gender: "", regNumber: "", birthdate: "",
  });
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [errors, setErrors] = useState({});
  const initializedRef = useRef(false);

  // Initialize form from profile (once)
  useEffect(() => {
    if (profile && !initializedRef.current) {
      initializedRef.current = true;
      setFormData((prev) => ({
        name: prev.name || profile.name || "",
        lastName: prev.lastName || profile.lastName || "",
        email: prev.email || profile.email || "",
        phone: prev.phone || profile.phoneNumber || "",
        gender: prev.gender || profile.gender || "",
        regNumber: prev.regNumber || profile.regNumber || "",
        birthdate: prev.birthdate || profile.birthdate || "",
      }));

      if (profile.birthdate) {
        const [y, m, d] = profile.birthdate.split("-");
        if (y && m && d) {
          setBirthYear(y);
          setBirthMonth(parseInt(m, 10).toString());
          setBirthDay(parseInt(d, 10).toString());
        }
      }
    }
  }, [profile]);

  // Reset day if it exceeds days in selected month
  useEffect(() => {
    if (birthYear && birthMonth && birthDay) {
      const max = new Date(parseInt(birthYear), parseInt(birthMonth), 0).getDate();
      if (parseInt(birthDay) > max) setBirthDay("");
    }
  }, [birthYear, birthMonth, birthDay]);

  // Sync birthdate dropdowns → formData.birthdate
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      birthdate: birthYear && birthMonth && birthDay
        ? `${birthYear}-${String(birthMonth).padStart(2, "0")}-${String(birthDay).padStart(2, "0")}`
        : "",
    }));
  }, [birthYear, birthMonth, birthDay]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }, [errors]);

  const getDaysInMonth = useCallback(() => {
    const max = birthYear && birthMonth
      ? new Date(parseInt(birthYear), parseInt(birthMonth), 0).getDate()
      : 31;
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [birthYear, birthMonth]);

  const validate = useCallback((hasPhoto) => {
    const errs = {};
    if (!formData.name?.trim()) errs.name = t.nameRequired;
    if (!formData.lastName?.trim()) errs.lastName = t.lastNameRequired;
    if (!hasPhoto) errs.photo = t.photoRequired;
    if (!formData.email?.trim()) errs.email = t.emailRequired;
    if (isTelegramUser && needsPhone && !formData.phone?.trim()) errs.phone = t.phoneRequired;
    if (!formData.gender) errs.gender = t.genderRequired;
    if (!formData.birthdate) errs.birthdate = t.birthdateRequired;
    if (isMongolia && !formData.regNumber?.trim()) errs.regNumber = t.regNumberRequired;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData, isTelegramUser, needsPhone]);

  const clearError = useCallback((field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  }, []);

  return {
    formData, errors, birthYear, birthMonth, birthDay,
    setBirthYear, setBirthMonth, setBirthDay,
    handleChange, getDaysInMonth, validate, clearError,
  };
}

function usePhotoUpload(profile, { updateProfile, hasTelegramId }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const resolvePhone = useCallback(() => {
    if (profile?.phoneNumber) return profile.phoneNumber;
    if (hasTelegramId || profile?.telegramId) {
      return `telegram_${profile?.telegramId || "unknown"}`;
    }
    return profileApi.getPhoneFromToken();
  }, [profile, hasTelegramId]);

  const upload = useCallback(async (file, formData) => {
    if (!file.type.startsWith("image/")) return { error: t.photoTypeError };
    if (file.size > MAX_PHOTO_SIZE) return { error: t.photoSizeError };

    const phone = resolvePhone();
    if (!phone) return { error: t.photoUploadError };

    setUploading(true);
    try {
      if (!profile) {
        await profileApi.createOrUpdateProfile({
          name: formData.name || null,
          lastName: formData.lastName || null,
          phone,
        });
      }

      const response = await profileApi.uploadProfilePhoto(phone, file);

      if (response.found && response.candidate) {
        updateProfile(response.candidate);
        return { success: t.photoUploadSuccess };
      }
      return { error: t.photoUploadError };
    } catch {
      return { error: t.photoUploadError };
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [profile, resolvePhone, updateProfile]);

  return { uploading, fileInputRef, upload };
}

// --- Sub-Components ---

function ProfileHeader({ canGoBack, onExit }) {
  const navigate = useNavigate();

  return (
    <div className="bg-black text-white p-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        {canGoBack && (
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition"
            type="button"
          >
            <IoArrowBack className="text-xl" />
          </button>
        )}
        <h1 className="text-xl font-bold">{t.profile}</h1>
      </div>
      <button
        onClick={onExit}
        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition text-sm font-medium"
        type="button"
      >
        <IoLogOutOutline className="text-lg" />
        {t.exit}
      </button>
    </div>
  );
}

function PhotoSection({ photoUrl, uploading, fileInputRef, onUpload, error }) {
  const triggerUpload = () => fileInputRef.current?.click();

  return (
    <div className="flex flex-col items-center mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {t.profilePhoto} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          type="file"
          ref={fileInputRef}
          onChange={onUpload}
          accept="image/*"
          className="hidden"
        />
        <button
          type="button"
          onClick={triggerUpload}
          disabled={uploading}
          className={`w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 shadow-lg overflow-hidden relative group cursor-pointer focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed ${
            error ? "border-red-400" : "border-white"
          }`}
        >
          {photoUrl ? (
            <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <BiSolidUser className="w-12 h-12 text-gray-400" />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
            <IoCameraOutline className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="animate-spin border-3 border-t-3 rounded-full border-white border-t-transparent w-8 h-8" />
            </div>
          )}
        </button>
        <div className="absolute bottom-0 right-0 w-8 h-8 bg-black rounded-full flex items-center justify-center border-2 border-white shadow-md">
          <IoCameraOutline className="w-4 h-4 text-white" />
        </div>
      </div>
      <button
        type="button"
        onClick={triggerUpload}
        disabled={uploading}
        className="mt-3 text-sm text-gray-600 hover:text-black transition-colors disabled:opacity-50"
      >
        {uploading ? t.uploadingPhoto : t.changePhoto}
      </button>
      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
    </div>
  );
}

function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function BirthdatePicker({ birthYear, birthMonth, birthDay, setBirthYear, setBirthMonth, setBirthDay, getDaysInMonth, error, clearError }) {
  const handleChange = (setter) => (e) => {
    setter(e.target.value);
    clearError("birthdate");
  };

  const selectClass = `w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white text-gray-700 appearance-none cursor-pointer ${
    error ? "border-red-400" : "border-gray-300"
  }`;

  return (
    <div className="md:col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <span className="flex items-center gap-2">
          <IoCalendarOutline className="text-gray-500" />
          {t.birthdate} <span className="text-red-500">*</span>
        </span>
      </label>
      <div className="grid grid-cols-3 gap-2">
        <select value={birthYear} onChange={handleChange(setBirthYear)} className={selectClass} style={SELECT_STYLE}>
          <option value="">{t.selectYear}</option>
          {BIRTH_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={birthMonth} onChange={handleChange(setBirthMonth)} className={selectClass} style={SELECT_STYLE}>
          <option value="">{t.selectMonth}</option>
          {t.months.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select value={birthDay} onChange={handleChange(setBirthDay)} className={selectClass} style={SELECT_STYLE}>
          <option value="">{t.selectDay}</option>
          {getDaysInMonth().map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      {birthYear && birthMonth && birthDay && (
        <p className="text-xs text-gray-500 mt-2 text-center">
          {birthYear}.{String(birthMonth).padStart(2, "0")}.{String(birthDay).padStart(2, "0")}
        </p>
      )}
      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
    </div>
  );
}

// --- Main Component ---

export default function Profile() {
  const navigate = useNavigate();
  const {
    profile, loading: profileLoading, saveProfile, updateProfile,
    needsPhone, needsNameCollection, needsProfileCompletion,
    needsEmail, needsGender, needsBirthdate, needsRegNumber,
    isTelegramUser, hasTelegramId,
  } = useContext(ProfileContext);

  const isProfileIncomplete = needsPhone || needsNameCollection || needsProfileCompletion ||
    needsEmail || needsGender || needsBirthdate || needsRegNumber;

  const [wasForced, setWasForced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const form = useProfileForm(profile, { isTelegramUser, needsPhone });
  const photo = usePhotoUpload(profile, { updateProfile, hasTelegramId });

  useEffect(() => {
    if (localStorage.getItem("redirectPath") || isProfileIncomplete) {
      setWasForced(true);
    }
  }, [isProfileIncomplete]);

  const showToast = useCallback((message, type) => setToast({ message, type }), []);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await photo.upload(file, form.formData);

    if (result.success) {
      showToast(result.success, "success");
      form.clearError("photo");
    } else {
      showToast(result.error, "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.validate(!!profile?.photoUrl)) return;

    setSaving(true);
    try {
      const success = await saveProfile(form.formData);

      if (success) {
        showToast(t.saveSuccess, "success");
        const redirectPath = localStorage.getItem("redirectPath");

        if (redirectPath) {
          setTimeout(() => {
            localStorage.removeItem("redirectPath");
            navigate(redirectPath);
          }, 500);
        } else if (wasForced) {
          setTimeout(() => navigate("/chat"), 500);
        }
      } else {
        showToast(t.saveError, "error");
      }
    } catch {
      showToast(t.saveError, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleExit = () => {
    Cookies.remove("chatToken");
    localStorage.clear();
    navigate("/login");
  };

  if (profileLoading && !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin border-4 border-t-4 rounded-full border-gray-300 border-t-gray-800 w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <ProfileHeader canGoBack={!isProfileIncomplete} onExit={handleExit} />

      <div className="max-w-2xl mx-auto p-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          <PhotoSection
            photoUrl={profile?.photoUrl}
            uploading={photo.uploading}
            fileInputRef={photo.fileInputRef}
            onUpload={handlePhotoChange}
            error={form.errors.photo}
          />

          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">{t.personalInfo}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={t.lastName} required error={form.errors.lastName}>
                <input
                  type="text"
                  name="lastName"
                  value={form.formData.lastName}
                  onChange={form.handleChange}
                  placeholder={t.lastNamePlaceholder}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                    form.errors.lastName ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </FormField>

              <FormField label={t.name} required error={form.errors.name}>
                <input
                  type="text"
                  name="name"
                  value={form.formData.name}
                  onChange={form.handleChange}
                  placeholder={t.namePlaceholder}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                    form.errors.name ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </FormField>

              <FormField label={t.email} required error={form.errors.email}>
                <input
                  type="email"
                  name="email"
                  value={form.formData.email}
                  onChange={form.handleChange}
                  placeholder={t.emailPlaceholder}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                    form.errors.email ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </FormField>

              <FormField label={t.phone} required error={form.errors.phone}>
                <input
                  type="text"
                  name="phone"
                  value={profile?.phoneNumber ? (profile.phoneNumber) : form.formData.phone}
                  onChange={profile?.phoneNumber ? undefined : form.handleChange}
                  disabled={!!profile?.phoneNumber}
                  placeholder={isMongolia ? "80201929" : "901234567"}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    profile?.phoneNumber
                      ? "border-gray-200 bg-gray-100 text-gray-500"
                      : (form.errors.phone ? "border-red-500" : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black")
                  }`}
                />
              </FormField>

              <FormField label={t.gender} required error={form.errors.gender}>
                <select
                  name="gender"
                  value={form.formData.gender}
                  onChange={form.handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                    form.errors.gender ? "border-red-400" : "border-gray-300"
                  }`}
                >
                  <option value="">{t.selectGender}</option>
                  <option value="male">{t.male}</option>
                  <option value="female">{t.female}</option>
                  <option value="other">{t.other}</option>
                </select>
              </FormField>

              {isMongolia && (
                <FormField label={t.regNumber} required error={form.errors.regNumber}>
                  <input
                    type="text"
                    name="regNumber"
                    value={form.formData.regNumber}
                    onChange={form.handleChange}
                    placeholder={t.regNumberPlaceholder}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      form.errors.regNumber ? "border-red-400" : "border-gray-300"
                    }`}
                  />
                </FormField>
              )}

              <BirthdatePicker
                birthYear={form.birthYear}
                birthMonth={form.birthMonth}
                birthDay={form.birthDay}
                setBirthYear={form.setBirthYear}
                setBirthMonth={form.setBirthMonth}
                setBirthDay={form.setBirthDay}
                getDaysInMonth={form.getDaysInMonth}
                error={form.errors.birthdate}
                clearError={form.clearError}
              />
            </div>
          </div>

          <div className="h-20 md:hidden" />

          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 md:relative md:border-0 md:p-0 md:bg-transparent z-40">
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-black text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-opacity-80 transition disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin border-2 border-t-2 rounded-full border-gray-300 border-t-white w-5 h-5" />
                  {t.saving}
                </>
              ) : (
                localStorage.getItem("redirectPath") ? t.save : t.saveOnly
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
}
